import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, buildWhatsappUrl } from "@/lib/api";
import { toast } from "sonner";
import { WhatsappLogo, CheckCircle, ChatCircleDots } from "@phosphor-icons/react";
import { PublicFieldRenderer, DISPLAY_ONLY, ytEmbed, evalCondition } from "@/components/fieldsBuilder";

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [waUrl, setWaUrl] = useState("");

  useEffect(() => {
    api.get(`/public/forms/${slug}`)
      .then(r => {
        setForm(r.data);
        const init = {};
        r.data.fields.forEach(f => {
          if (DISPLAY_ONLY.includes(f.type)) return;
          if (f.type === "checkbox") init[f.id] = [];
          else if (f.type === "rating") init[f.id] = 0;
          else init[f.id] = "";
        });
        setAnswers(init);
      })
      .catch(() => setForm(false))
      .finally(() => setLoading(false));
  }, [slug]);

  const setAns = (id, val) => setAnswers(a => ({ ...a, [id]: val }));

  const uploadFile = async (fieldId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/public/forms/${slug}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles(f => ({ ...f, [fieldId]: { name: data.filename, url: `${window.location.origin}/api/files/${data.path}` } }));
      setAns(fieldId, data.filename);
      toast.success("File diunggah");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload");
    }
  };

  const buildMessage = () => {
    const lines = [];
    if (form.intro_message) lines.push(form.intro_message, "");
    lines.push(`*${form.title}*`);
    form.fields.forEach(f => {
      if (DISPLAY_ONLY.includes(f.type)) return;
      if (!evalCondition(f, answers)) return;
      let val = answers[f.id];
      if (Array.isArray(val)) val = val.join(", ");
      if (f.type === "rating") val = val ? `${val}/${f.max_rating || 5} ⭐` : "-";
      if (val === "" || val == null) val = "-";
      if (f.type === "file" && files[f.id]?.url) {
        val = `${files[f.id].name}\n   ${files[f.id].url}`;
      }
      lines.push(`• ${f.label}: ${val}`);
    });
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const f of form.fields) {
      if (DISPLAY_ONLY.includes(f.type)) continue;
      if (!evalCondition(f, answers)) continue;
      const v = answers[f.id];
      const empty = (v == null) || v === "" || (Array.isArray(v) && v.length === 0) || (f.type === "rating" && !v);
      if (f.required && empty) {
        toast.error(`"${f.label}" wajib diisi`);
        return;
      }
    }
    setSubmitting(true);
    try {
      // Strip hidden field answers so backend routing & sheets only see visible answers
      const visibleAnswers = {};
      for (const f of form.fields) {
        if (DISPLAY_ONLY.includes(f.type)) continue;
        if (!evalCondition(f, answers)) continue;
        if (f.id in answers) visibleAnswers[f.id] = answers[f.id];
      }
      const { data } = await api.post(`/public/forms/${slug}/submit`, { answers: visibleAnswers });
      const targetNumber = data?.routed_to?.whatsapp_number || form.whatsapp_number;
      const url = buildWhatsappUrl(targetNumber, buildMessage());
      setWaUrl(url);
      setDone(true);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-12 font-display text-2xl">Memuat…</div>;
  if (!form) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="nb-card p-8 text-center max-w-md">
        <h1 className="font-display font-black text-3xl">Formulir tidak ditemukan</h1>
        <p className="text-[#4A4A4A] mt-2">Formulir ini mungkin telah dihapus atau dinonaktifkan.</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="nb-card nb-card-lg p-8 text-center max-w-md fade-in">
        <div className="w-16 h-16 bg-[#A7E4C0] border-2 border-[#121212] rounded-full flex items-center justify-center mx-auto">
          <CheckCircle size={36} weight="bold" />
        </div>
        <h1 className="font-display font-black text-3xl mt-4">{form.success_message}</h1>
        <p className="text-[#4A4A4A] mt-2">Jika WhatsApp belum terbuka, klik tombol di bawah.</p>
        <a href={waUrl} target="_blank" rel="noreferrer" className="nb-btn nb-btn-primary text-lg mt-6 justify-center w-full" data-testid="public-form-wa-button">
          <WhatsappLogo size={20} weight="bold" /> Kirim ke WhatsApp
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-[#A7E4C0] border-2 border-[#121212] rounded-lg flex items-center justify-center">
            <ChatCircleDots size={20} weight="bold" />
          </div>
          <span className="font-display font-black text-xl">Layanan lainnya RS Mulya</span>
        </div>
        <div className="nb-card nb-card-lg p-7">
          <span className="nb-badge">Formulir</span>
          <h1 className="font-display font-black text-3xl tracking-tight mt-3" data-testid="public-form-title">{form.title}</h1>
          {form.description && <p className="text-[#4A4A4A] mt-2">{form.description}</p>}

          <form onSubmit={submit} className="space-y-4 mt-6">
            {form.fields.filter(f => evalCondition(f, answers)).map(f => (
              <PublicFieldRenderer
                key={f.id}
                field={f}
                value={answers[f.id]}
                onChange={(v) => setAns(f.id, v)}
                uploadFile={uploadFile}
                uploaded={files[f.id]}
                ytEmbed={ytEmbed}
              />
            ))}
            <button type="submit" disabled={submitting} className="nb-btn nb-btn-primary w-full justify-center text-lg" data-testid="public-form-submit">
              <WhatsappLogo size={20} weight="bold" /> {submitting ? "Memproses…" : "Kirim via WhatsApp"}
            </button>
            <p className="text-xs text-center text-[#4A4A4A]">
              Tombol di atas akan membuka WhatsApp dengan pesan terformat.
            </p>
          </form>
        </div>
        <p className="text-center text-xs text-[#4A4A4A] mt-6">
          Dibuat dengan <strong>Layanan lainnya RS Mulya</strong>
        </p>
      </div>
    </div>
  );
}
